import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CButton, CForm, CFormSelect, CModal, CModalBody } from '@coreui/react-pro';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { callApi } from '../../apiCaller';
import {
     toastError,
     checkLicenseeUserAndCart,
     getCountriesList,
     getListTimeZonesMoment,
     addTimezoneLocal,
     getListOptionsCountryByCountry,
} from '../../utils';
import { API_SUBSCRIBER_PROFILE, LOCAL_CART, LOCAL_LICENSEE } from '../../constants';

import { SelectSearchField } from '../../components/General/Forms';

import { setSubscriberLicenseeId } from '../../components/CMS/Subscriber/Licensees/actions';
import { setCurrentCountry } from '../../components/CMS/Subscriber/GlobalStates/CurrentCountry/actions';
import { setSubscriberUser } from '../../components/CMS/Subscriber/GlobalStates/CurrentUser/actions';
import { fetchSubscriberProductsRequest } from '../../components/CMS/Subscriber/Products/actions';

const validationSchema = () => {
     return Yup.object().shape({
          country: Yup.string().required('Required.'),
     });
};

const ConfirmCountry = () => {
     const dispatch = useDispatch();

     const user = useSelector((state) => state.subscriber.user);
     const licensees = useSelector((state) => state.subscriber.licensees);
     const products = useSelector((state) => state.subscriber.products.list);
     const currentLocation = useSelector((state) => state.subscriber.currentLocation || {});

     const { country_code, timezone } = currentLocation;
     const licenseesOptions =
          user?.country && !user.timezone
               ? getListOptionsCountryByCountry(user?.country, licensees).map(({ id, name }) => ({ value: id, label: name }))
               : getCountriesList(licensees);

     const licenseeByIp = licenseesOptions?.find((item) => item.value === country_code) || '';

     const defaultCountry = licenseeByIp ? licenseeByIp.value : licensees?.length > 0 ? licenseesOptions[0]?.value : '';

     const listTimezonesDefault = getListTimeZonesMoment(defaultCountry || licenseesOptions[0]?.value);

     const defaultTimeZone = defaultCountry && timezone ? listTimezonesDefault?.find(({ value }) => value.includes(timezone))?.value : '';

     const userCountry = user?.country;
     const userTimezone = user?.timezone;

     const showPopup = !userCountry || (userCountry && licensees && !licensees.some(({ country }) => country.includes(userCountry))) || !userTimezone;

     const [listTimezones, setListTimezones] = useState([]);

     const initialValues = {
          country: defaultCountry,
          timezone: defaultTimeZone ? defaultTimeZone : listTimezonesDefault?.length > 0 ? listTimezonesDefault[0].value : '',
     };

     const handleFormSubmit = (values, { setSubmitting }) => {
          const licenseeIdSelected = licensees.find(({ country }) => country.includes(values.country))?.id || '';
          const cartLocalStorage = JSON.parse(localStorage.getItem(LOCAL_CART));

          callApi(API_SUBSCRIBER_PROFILE, 'PUT', values).then((response) => {
               setSubmitting(false);
               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               const updatedUser = response.data;
               const newUser = {
                    ...user,
                    ...updatedUser,
               };

               addTimezoneLocal(newUser.timezone);
               dispatch(setSubscriberUser(newUser));
               dispatch(setCurrentCountry(newUser.country));
               checkLicenseeUserAndCart(cartLocalStorage, licenseeIdSelected, products);
               localStorage.removeItem(LOCAL_LICENSEE);

               dispatch(setSubscriberLicenseeId(licenseeIdSelected));
          });
     };

     const fetchProducts = () => {
          if (!products.length) {
               dispatch(fetchSubscriberProductsRequest());
          }
     };
     useEffect(() => {
          fetchProducts();
     }, []); // eslint-disable-line react-hooks/exhaustive-deps

     return (
          <CModal alignment="center" visible={showPopup} className="popup-custom confirm-countryTimezone-popup">
               <CModalBody>
                    <h3 className="mb-3 popup-title">Please Confirm Your Country & Timezone</h3>
                    <p className="popup-description">
                         Products may differ by country. Please select and confirm your country before browsing products.
                    </p>
                    {licensees?.length > 0 && (
                         <Formik
                              initialValues={initialValues}
                              validationSchema={validationSchema}
                              onSubmit={handleFormSubmit}
                              validateOnChange={false}
                              validateOnBlur={false}
                              enableReinitialize
                         >
                              {({ values, errors, handleSubmit, isSubmitting, setFieldValue }) => {
                                   let newListTimezones = [...listTimezones];
                                   if (listTimezones.length <= 0) {
                                        newListTimezones = [...listTimezonesDefault];
                                   }

                                   const timezoneValue = newListTimezones.find((tz) => tz.value === values.timezone) || { value: '', label: '' };

                                   const handleTimeZone = (e) => {
                                        setFieldValue('timezone', e.value);
                                   };

                                   const handleChangeCountry = (e) => {
                                        const countryValue = e.target.value;
                                        const newListTimezone = getListTimeZonesMoment(countryValue);
                                        setListTimezones(newListTimezone);
                                        setFieldValue('timezone', newListTimezone[0].value);
                                        setFieldValue('country', countryValue);
                                   };

                                   return (
                                        <CForm onSubmit={handleSubmit} noValidate>
                                             <CFormSelect
                                                  aria-label="Default select"
                                                  name="country"
                                                  id="country"
                                                  value={values.country}
                                                  error={errors.country}
                                                  options={licenseesOptions}
                                                  onChange={handleChangeCountry}
                                                  disabled={isSubmitting}
                                                  className="mb-3 input-custom select-search-custom"
                                             />
                                             <SelectSearchField
                                                  classNameWrap="input-custom-wrap"
                                                  classNameInput="input-custom select-search-custom"
                                                  placeholder="Select Time Zone..."
                                                  options={listTimezones.length <= 0 ? listTimezonesDefault : listTimezones}
                                                  selectInputType={false}
                                                  onChange={handleTimeZone}
                                                  error={errors.timezoneValue}
                                                  value={timezoneValue}
                                                  isRequired
                                             />
                                             <div className="mt-3 text-center">
                                                  <CButton
                                                       className="btn-blue-highlight btn-round-9 btn-h-50 text-uppercase"
                                                       type="submit"
                                                       disabled={isSubmitting}
                                                  >
                                                       Confirm My Country & Timezone
                                                  </CButton>
                                             </div>
                                        </CForm>
                                   );
                              }}
                         </Formik>
                    )}
               </CModalBody>
          </CModal>
     );
};

export default ConfirmCountry;
